import { useEffect, useMemo, useRef } from "react";
import { Outlet } from "react-router";

import { Box, Typography } from "@mui/material";

import { announcementsAtom } from "@/src/state/announcementAtom";
import { localeAtom } from "@/src/state/localeAtom";

import {
  AnnouncementContainer,
  Footer,
  Header,
  NotificationContainer
} from "@/src/components/organisms";

import ContentRouterWidget from "@almamedia/ict-content-router-react";

import { useAuth } from "@/src/auth/hooks/useAuth";
import { useAuthCheckOnRouteChange } from "@/src/auth/hooks/useAuthCheckOnRouteChange";
import { useAuthCheckTimer } from "@/src/auth/hooks/useAuthCheckTimer";
import { configureAmplify } from "@/src/config/amplify";
import { useClearAuctionState } from "@/src/hooks/useClearAuctionState";
import useHotjar from "@/src/hooks/useHotjar";
import useIotConnectionRecovery from "@/src/hooks/useIotConnectionRecovery";
import { useZarazTracking } from "@/src/hooks/useZarazTracking";
import { changeLocale } from "@/src/localizations";
import { DigitalDataLayerProvider } from "@/src/providers/DigitalDataLayerProvider";
import { getAnnouncements } from "@/src/utils/announcementUtils";
import {
  subscribeToTopics,
  unsubscribeFromTopics,
  useIOT
} from "@/src/utils/iot";

import { ErrorBoundary } from "@sentry/react";
import { useAtomValue, useSetAtom } from "jotai";

import "@/src/styles/App.scss";

export default function AppLayout() {
  useAuthCheckTimer();
  useAuthCheckOnRouteChange();
  useZarazTracking();
  useHotjar();

  const locale = useAtomValue(localeAtom);
  const { isAuthenticated, isAuthenticating, isDealer, user } = useAuth();
  const { clearAllAuctionState } = useClearAuctionState();
  const wasAuthenticatedRef = useRef(false);
  const setAnnouncements = useSetAtom(announcementsAtom);

  const { handleSocketMessage } = useIOT();
  const subscribedToIOTTopicsRef = useRef(false);

  // Re-establish the IoT WebSocket and refetch auction data after device
  // sleep/wake or network loss, so dealers never miss live bid updates.
  useIotConnectionRecovery(isAuthenticated);

  const authForDigitalData = useMemo(
    () => ({ isAuthenticated, isAuthenticating, user }),
    [isAuthenticated, isAuthenticating, user]
  );

  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      clearAllAuctionState();

      // Tear down the IoT subscription and reset the guard so a subsequent
      // login (possibly as a different user) re-subscribes to the correct
      // user-specific topics instead of reusing the previous session's.
      unsubscribeFromTopics();
      subscribedToIOTTopicsRef.current = false;
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, clearAllAuctionState]);

  useEffect(() => {
    changeLocale(locale);
  }, [locale]);

  useEffect(() => {
    const getInitialAnnouncements = async () => {
      const announcements = await getAnnouncements();
      setAnnouncements(announcements);
    };
    getInitialAnnouncements();
  }, []);

  useEffect(() => {
    if (isAuthenticated && !subscribedToIOTTopicsRef.current) {
      configureAmplify();

      const topics = [`users/${user?.id}`, "announcements"];
      if (isDealer) {
        topics.push("auctions");
      }

      // Mark as subscribed before the async call resolves so a re-render
      // cannot slip through the guard and create a duplicate subscription.
      subscribedToIOTTopicsRef.current = true;

      subscribeToTopics(topics, (data) => {
        // Handle IOT message
        handleSocketMessage(data);
      }).catch((error) => {
        // Reset the guard so the subscription can be re-attempted instead of
        // being stuck "subscribed" without a live connection.
        subscribedToIOTTopicsRef.current = false;
        console.error("[Baana IOT] initial topic subscription failed", error);
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      window.scrollTo(0, 0);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <>
      <ErrorBoundary
        fallback={
          <Box
            sx={{
              py: 0.5,
              px: 1,
              "& .MuiTypography-root": {
                fontSize: "0.75rem",
                color: "text.secondary"
              }
            }}
            role="status"
            aria-live="polite"
          >
            <Typography variant="caption">
              Analytics temporarily unavailable
            </Typography>
          </Box>
        }
        beforeCapture={(scope) =>
          scope.setTag("component", "ContentRouterWidgets")
        }
      >
        <ContentRouterWidget slot="ALMA_DATALAYER_SENDER" />
      </ErrorBoundary>
      <Header />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 64px)"
        }}
      >
        <Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
          <DigitalDataLayerProvider locale={locale} auth={authForDigitalData}>
            <Outlet />
          </DigitalDataLayerProvider>
        </Box>
        <Footer />
      </Box>
      <AnnouncementContainer />
      <NotificationContainer />
    </>
  );
}
